/**
 * Merchant Pro V1 PR-4c — Shared WebFloorPlanCanvas component.
 *
 * Operator-locked discipline (PR-4c §10.4 cross-walk, 4c-AC#6/#7/#8/#9):
 *
 *   • Canonical web rendering surface for any floor-plan view.
 *     Consumed by LiveFloorPlanPage AND ManualBookingPage S3.
 *   • Wraps a CSS grid of <ChairGridTableCard> with room-edge corner
 *     labels (STAGE / WALL / BAR / ENTRANCE) per the `room` data —
 *     folds in deferred P2-PR3-A.
 *   • Mobile parity boundary: mobile FloorPlanCanvas.tsx renders the
 *     same corner labels at canvas edges via SVG. This web component
 *     mirrors the placement contract using absolutely-positioned
 *     <span> chips so the visual maps 1:1.
 *   • Role-agnostic: NO role conditionals inside this component.
 *     Parents decide what `onTableClick` does (Owner: open
 *     TableActionSheet; Manual Booking S3: select target). Future
 *     Front Desk surfaces consume this component verbatim.
 *
 * Props (all optional except `tables`):
 *   tables          — Table[] (must include `id`, `state`, `capacity`,
 *                     optional `current_party_size`).
 *   room            — { stage_position, wall_position, bar_position,
 *                       entrance_position } — corner labels.
 *   selectedTableId — currently-selected table.id (highlight).
 *   onTableClick    — (table) => void. Receives full row.
 *   disabledFn      — (table) => boolean. Disabled cards get 45 % alpha
 *                     + no onClick. Useful for "too small" / "not
 *                     available" Manual-Booking semantics.
 *   testIdPrefix    — testid prefix for <ChairGridTableCard>.
 *   minCardWidth    — CSS-grid minmax. Default 180 (canonical).
 *   emptyMessage    — string for the empty state.
 *   children        — optional slot rendered above the grid (e.g.
 *                     toolbar / chips).
 */
import { Theater, Building, Wine, DoorOpen } from 'lucide-react';
import ChairGridTableCard from '@/components/owner/ChairGridTableCard';

const CORNER_ICON = {
  stage:    Theater,
  wall:     Building,
  bar:      Wine,
  entrance: DoorOpen,
};

const CORNER_LABEL = {
  stage:    'STAGE / TV',
  wall:     'WALL',
  bar:      'BAR',
  entrance: 'ENTRANCE',
};

/* Mobile parity (FloorPlanCanvas.tsx §66-89): label sits at one of four
 * edges. We translate {top|bottom|left|right} to absolute CSS classes
 * over the inner canvas wrap.
 */
const EDGE_CLASSES = {
  top:    'top-2 left-1/2 -translate-x-1/2',
  bottom: 'bottom-2 left-1/2 -translate-x-1/2',
  left:   'left-2 top-1/2 -translate-y-1/2 -rotate-90 origin-center',
  right:  'right-2 top-1/2 -translate-y-1/2 rotate-90 origin-center',
};

function CornerChip({ kind, edge, testIdPrefix }) {
  if (!edge) return null;
  const Icon = CORNER_ICON[kind];
  return (
    <span
      data-testid={`${testIdPrefix}-corner-${kind}`}
      className={`absolute z-10 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#0E0E13]/85 border border-gold/25 text-gold text-[10px] uppercase tracking-widest font-semibold whitespace-nowrap pointer-events-none ${EDGE_CLASSES[edge]}`}
    >
      <Icon size={11} />
      {CORNER_LABEL[kind]}
    </span>
  );
}

export default function WebFloorPlanCanvas({
  tables = [],
  room = null,
  selectedTableId = null,
  onTableClick = null,
  disabledFn = null,
  testIdPrefix = 'web-floor-plan',
  minCardWidth = 180,
  emptyMessage = 'No tables in this room.',
  children = null,
}) {
  const positions = {
    stage:    room?.stage_position || null,
    wall:     room?.wall_position || null,
    bar:      room?.bar_position || null,
    entrance: room?.entrance_position || null,
  };
  const hasAnyCorner =
    positions.stage || positions.wall || positions.bar || positions.entrance;

  return (
    <div
      data-testid={`${testIdPrefix}-canvas`}
      data-room-id={room?.id || null}
      className="relative rounded-3xl border border-gold/15 bg-[#0E0E13]/70 overflow-hidden"
      style={{
        backgroundImage:
          'radial-gradient(circle at top right, rgba(212,175,55,0.06), transparent 60%), radial-gradient(circle at bottom left, rgba(212,175,55,0.03), transparent 55%)',
      }}
    >
      {/* Corner labels (only those the room actually defines) */}
      {hasAnyCorner ? (
        <>
          <CornerChip kind="stage"    edge={positions.stage}    testIdPrefix={testIdPrefix} />
          <CornerChip kind="wall"     edge={positions.wall}     testIdPrefix={testIdPrefix} />
          <CornerChip kind="bar"      edge={positions.bar}      testIdPrefix={testIdPrefix} />
          <CornerChip kind="entrance" edge={positions.entrance} testIdPrefix={testIdPrefix} />
        </>
      ) : null}

      {children ? (
        <div
          className="px-5 pt-5"
          data-testid={`${testIdPrefix}-toolbar`}
        >
          {children}
        </div>
      ) : null}

      {tables.length === 0 ? (
        <div
          className="px-6 py-16 text-center text-sm text-white/45"
          data-testid={`${testIdPrefix}-empty`}
        >
          {emptyMessage}
        </div>
      ) : (
        <div
          className="grid gap-4 p-6 sm:p-8"
          style={{ gridTemplateColumns: `repeat(auto-fill, minmax(${minCardWidth}px, 1fr))` }}
          data-testid={`${testIdPrefix}-grid`}
        >
          {tables.map((t) => {
            const isDisabled = disabledFn ? !!disabledFn(t) : false;
            const handler = onTableClick && !isDisabled
              ? () => onTableClick(t)
              : null;
            return (
              <div
                key={t.id}
                data-testid={`${testIdPrefix}-cell-${t.id}`}
                style={{ opacity: isDisabled ? 0.45 : 1 }}
              >
                <ChairGridTableCard
                  table={t}
                  selected={selectedTableId === t.id}
                  onClick={handler}
                  testIdPrefix={testIdPrefix}
                />
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
